import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useCart } from "@/lib/cart";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import CartAddedDialog from "@/components/app/CartAddedDialog";
import { can } from "@/lib/perms";
import {
  LayoutDashboard, BookOpen, CalendarDays, ShoppingCart, ClipboardList,
  Users, GraduationCap, LogOut, Menu, X, UserCog, FileText, ScrollText, FolderArchive, LifeBuoy, MessageCircle, Bell, Package, Wallet, Receipt, TicketPercent, Building2, UserPlus, BarChart3, ChevronDown, IdCard, ShieldCheck, Send
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

const PARTNER_NAV = [
  { to: "/partner", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-partner-dashboard" },
  { to: "/partner/catalogo", label: "Catalogo corsi", icon: BookOpen, testid: "nav-partner-catalog" },
  { to: "/partner/calendario", label: "Calendario", icon: CalendarDays, testid: "nav-partner-calendar" },
  { to: "/partner/carrello", label: "Carrello", icon: ShoppingCart, testid: "nav-partner-cart", badgeKey: "cart" },
  { to: "/partner/ordini", label: "Ordini", icon: ClipboardList, testid: "nav-partner-orders" },
  { to: "/partner/ricarica", label: "Ricarica", icon: Wallet, testid: "nav-partner-recharge" },
  { to: "/partner/iscrizioni", label: "Iscrizioni", icon: UserCog, testid: "nav-partner-enrollments" },
  { to: "/partner/documenti", label: "Documenti", icon: FolderArchive, testid: "nav-partner-documents" },
];

const CLIENT_NAV = [
  { to: "/cliente", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-cliente-dashboard" },
  { to: "/cliente/catalogo", label: "Catalogo corsi", icon: BookOpen, testid: "nav-cliente-catalogo", hideForSlot: true },
  { to: "/cliente/calendario", label: "Calendario", icon: CalendarDays, testid: "nav-cliente-calendar", hideForSlot: true },
  { to: "/cliente/iscrizioni", label: "Iscrizioni", icon: UserPlus, testid: "nav-cliente-iscrizioni" },
  { to: "/cliente/storico", label: "Storico iscrizioni", icon: ClipboardList, testid: "nav-cliente-storico" },
  { to: "/cliente/profilo", label: "Profilo Azienda", icon: Building2, testid: "nav-cliente-profilo" },
];

const ADMIN_NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-admin-dashboard", perm: "dashboard" },
  { group: "Catalogo" },
  { to: "/admin/corsi", label: "Catalogo corsi", icon: BookOpen, testid: "nav-admin-courses", perm: "courses" },
  { to: "/admin/edizioni", label: "Calendario edizioni", icon: CalendarDays, testid: "nav-admin-editions", perm: "editions" },
  { group: "Partner" },
  { to: "/admin/partner", label: "Gestione partner", icon: Users, testid: "nav-admin-partners", perm: "partners" },
  { to: "/admin/ordini", label: "Ordini", icon: ClipboardList, testid: "nav-admin-orders", badgeKey: "orders", perm: "orders" },
  { to: "/admin/iscrizioni", label: "Iscrizioni Partner", icon: UserCog, testid: "nav-admin-enrollments", perm: "enrollments_partner" },
  { to: "/admin/ricariche", label: "Ricariche credito", icon: Receipt, testid: "nav-admin-recharges", badgeKey: "recharges", perm: "recharges" },
  { to: "/admin/pacchetti", label: "Pacchetti ricarica", icon: Package, testid: "nav-admin-packages", perm: "recharges" },
  { to: "/admin/codici-promo", label: "Codici promo", icon: TicketPercent, testid: "nav-admin-promo-codes", perm: "recharges" },
  { group: "Clienti" },
  { to: "/admin/clienti", label: "Clienti", icon: Building2, testid: "nav-admin-clients", perm: "clients" },
  { to: "/admin/clienti-iscrizioni", label: "Iscrizioni clienti", icon: UserPlus, testid: "nav-admin-client-enrollments", badgeKey: "clientEnrollments", perm: "enrollments_clients" },
  { to: "/admin/clienti-report", label: "Report clienti", icon: BarChart3, testid: "nav-admin-client-reports", perm: "client_reports" },
  { to: "/admin/richieste-attivazione", label: "Richieste Attivazione Corsi", icon: Send, testid: "nav-admin-activation-requests", badgeKey: "activationRequests", perm: "activation_requests" },
  { group: "Dipendenti" },
  { to: "/admin/dipendenti", label: "Elenco Dipendenti", icon: IdCard, end: true, testid: "nav-admin-staff", perm: "employees" },
  { to: "/admin/dipendenti/nuovo", label: "Nuovo Dipendente", icon: UserPlus, testid: "nav-admin-staff-new", perm: "employees" },
  { to: "/admin/dipendenti/ruoli", label: "Ruoli e Permessi", icon: ShieldCheck, testid: "nav-admin-staff-roles", perm: "employees" },
  { group: "Sistema" },
  { to: "/admin/documenti", label: "Documenti", icon: FolderArchive, testid: "nav-admin-documents", perm: "documents" },
  { to: "/admin/log", label: "Log attività", icon: ScrollText, testid: "nav-admin-logs", perm: "system_config" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [unreadTickets, setUnreadTickets] = useState(0);
  const [unseenOrders, setUnseenOrders] = useState(0);
  const [unseenExtra, setUnseenExtra] = useState({ recharges: 0, clientEnrollments: 0, activationRequests: 0 });
  const prevUnseenOrdersRef = useRef(null);
  const firstLoadRef = useRef(true);
  const isCliente = user?.role === "cliente";
  const isAdmin = user?.role === "admin";
  const nav = isAdmin
    ? ADMIN_NAV.filter((it, i, arr) => {
        if (!it.group) return can(user, it.perm);
        for (let j = i + 1; j < arr.length && !arr[j].group; j += 1) if (can(user, arr[j].perm)) return true;
        return false;
      })
    : isCliente ? CLIENT_NAV.filter((it) => !(it.hideForSlot && user?.client?.billing_mode === "slot")) : PARTNER_NAV;
  const showSupport = !isCliente && (!isAdmin || can(user, "support"));
  const [collapsed, setCollapsed] = useState(() => {
    try { return JSON.parse(localStorage.getItem("athena_nav_collapsed") || "{}"); } catch { return {}; }
  });
  const toggleGroup = (g) => setCollapsed((c) => {
    const next = { ...c, [g]: !c[g] };
    localStorage.setItem("athena_nav_collapsed", JSON.stringify(next));
    return next;
  });
  const groupOf = (idx) => { for (let i = idx; i >= 0; i -= 1) if (nav[i].group) return nav[i].group; return null; };
  const roleLabel = user?.role === "admin" ? (user?.staff_role === "dipendente" ? "Dipendente" : "Amministratore") : isCliente ? "Cliente" : "Partner";
  const supportPath = user?.role === "admin" ? "/admin/supporto" : "/partner/supporto";

  const doLogout = async () => { await logout(); navigate("/login", { replace: true }); };

  const loadBadges = useCallback(async () => {
    if (!user || user.role === "cliente") return;
    if (user.role !== "admin" || can(user, "support")) try {
      const t = await api.get("/tickets/unread-count");
      setUnreadTickets(t.data?.count || 0);
    } catch (e) { /* noop */ }
    if (user.role === "admin") {
      if (can(user, "orders")) try {
        const o = await api.get("/orders/unseen-count");
        const next = o.data?.count || 0;
        const prev = prevUnseenOrdersRef.current;
        // Non invasive toast se nuovi ordini pagati arrivano mentre siamo fuori da /admin/ordini
        if (!firstLoadRef.current && prev !== null && next > prev && location.pathname !== "/admin/ordini") {
          const delta = next - prev;
          toast(
            delta === 1
              ? "Nuovo ordine pagato in arrivo"
              : `${delta} nuovi ordini pagati in arrivo`,
            {
              description: `Hai ${next} ordini da gestire.`,
              icon: <Bell className="w-4 h-4 text-amber-600" />,
              duration: 7000,
              action: {
                label: "Vai agli ordini",
                onClick: () => navigate("/admin/ordini"),
              },
              id: "new-orders-toast",
            }
          );
        }
        prevUnseenOrdersRef.current = next;
        firstLoadRef.current = false;
        setUnseenOrders(next);
      } catch (e) { /* noop */ }
      try {
        const [rc, ce, ar] = await Promise.all([
          can(user, "recharges") ? api.get("/recharges/unseen-count") : { data: { count: 0 } },
          can(user, "enrollments_clients") ? api.get("/client-enrollments/unseen-count") : { data: { count: 0 } },
          can(user, "activation_requests") ? api.get("/activation-requests/unseen-count") : { data: { count: 0 } },
        ]);
        setUnseenExtra({ recharges: rc.data?.count || 0, clientEnrollments: ce.data?.count || 0, activationRequests: ar.data?.count || 0 });
      } catch (e) { /* noop */ }
    }
  }, [user, location.pathname, navigate]);

  useEffect(() => { loadBadges(); }, [loadBadges, location.pathname]);
  useEffect(() => {
    if (!user) return;
    const id = setInterval(loadBadges, 45000);
    const onRefresh = () => loadBadges();
    window.addEventListener("badges:refresh", onRefresh);
    return () => {
      clearInterval(id);
      window.removeEventListener("badges:refresh", onRefresh);
    };
  }, [user, loadBadges]);

  const badgeFor = (k) => {
    if (k === "orders") return unseenOrders;
    if (k === "recharges") return unseenExtra.recharges;
    if (k === "clientEnrollments") return unseenExtra.clientEnrollments;
    if (k === "activationRequests") return unseenExtra.activationRequests;
    if (k === "cart") return cartCount;
    return 0;
  };

  const cart = useCart();
  const cartCount = cart?.totalQty || 0;

  const Sidebar = (
    <aside className="w-64 h-full shrink-0 brand-gradient text-white flex flex-col">
      <div className="p-5 border-b border-white/10 flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-white/95 flex items-center justify-center p-1">
          <img src="/logo-athena.png" alt="Centro Studi Athena" className="w-full h-full object-contain" />
        </div>
        <div>
          <div className="font-display font-bold">Athena Portal</div>
          <div className="text-xs text-slate-300">{roleLabel}</div>
        </div>
        <button className="ml-auto lg:hidden" onClick={() => setOpen(false)} data-testid="sidebar-close">
          <X className="w-5 h-5" />
        </button>
      </div>
      <nav className="p-3 flex-1 space-y-1 overflow-y-auto">
        {nav.map((item, idx) => {
          if (item.group) {
            const isOpen = !collapsed[item.group];
            const members = [];
            for (let i = idx + 1; i < nav.length && !nav[i].group; i += 1) members.push(nav[i]);
            const sum = members.reduce((s, m) => s + (m.badgeKey ? badgeFor(m.badgeKey) : 0), 0);
            return (
              <button key={item.group} type="button" onClick={() => toggleGroup(item.group)} aria-expanded={isOpen}
                className="w-full flex items-center gap-2 px-3 pt-4 pb-1 text-[10px] font-bold uppercase tracking-[0.18em] text-amber-400/80 hover:text-amber-300 transition-colors"
                data-testid={`nav-group-${item.group.toLowerCase()}`}>
                <span className="flex-1 text-left">{item.group}</span>
                {!isOpen && sum > 0 && <span className="inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] tracking-normal" data-testid={`nav-group-badge-${item.group.toLowerCase()}`}>{sum > 99 ? "99+" : sum}</span>}
                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${isOpen ? "" : "-rotate-90"}`} />
              </button>
            );
          }
          const g = groupOf(idx);
          if (g && collapsed[g]) return null;
          const badge = item.badgeKey ? badgeFor(item.badgeKey) : 0;
          return (
            <NavLink key={item.to} to={item.to} end={item.end}
              data-testid={item.testid}
              onClick={() => setOpen(false)}
              className={({ isActive }) => `sidebar-item ${isActive ? "active" : ""} flex items-center`}>
              <item.icon className="w-4 h-4" />
              <span className="flex-1 ml-2">{item.label}</span>
              {badge > 0 && (
                <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-xs font-bold" data-testid={`nav-badge-${item.badgeKey}`}>
                  {badge > 99 ? "99+" : badge}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>
      <div className="p-3 border-t border-white/10">
        <div className="px-3 py-2 mb-2">
          <div className="text-xs text-slate-400">Connesso come</div>
          <div className="text-sm font-medium truncate" data-testid="current-user-email">{user?.email}</div>
        </div>
        <button onClick={doLogout} data-testid="logout-button"
          className="sidebar-item w-full text-left">
          <LogOut className="w-4 h-4" /> Esci
        </button>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen flex bg-[#F4F6FB]">
      <div className="hidden lg:block sticky top-0 h-screen">{Sidebar}</div>
      {open && (
        <div className="fixed inset-0 z-40 flex lg:hidden">
          <div className="fixed inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="relative z-50 h-full">{Sidebar}</div>
        </div>
      )}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-14 bg-white border-b border-slate-200 flex items-center px-4 lg:px-8 gap-3">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="sidebar-open">
            <Menu className="w-5 h-5" />
          </button>
          <div className="font-display font-semibold text-slate-800 truncate" data-testid="topbar-area-title">
            {user?.role === "admin" ? "Centro Studi Athena · Console Amministrativa" : (() => {
              const area = isCliente ? "Area Cliente" : "Area Partner";
              const org = (isCliente ? user?.client : user?.partner)?.company_name;
              return org ? `${org} - ${area}` : area;
            })()}
          </div>
          <div className="ml-auto flex items-center gap-3">
            <div className="text-sm text-slate-500 hidden sm:block">
              {new Date().toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            </div>
            {showSupport && <button
              onClick={() => navigate(supportPath)}
              className="relative inline-flex items-center justify-center w-10 h-10 rounded-full bg-amber-50 hover:bg-amber-100 border border-amber-200 text-amber-700 transition"
              data-testid="topbar-support-button"
              title="Assistenza · Ticket"
              aria-label="Assistenza"
            >
              <MessageCircle className="w-5 h-5" />
              {unreadTickets > 0 && (
                <span className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-xs font-bold ring-2 ring-white" data-testid="topbar-support-badge">
                  {unreadTickets > 99 ? "99+" : unreadTickets}
                </span>
              )}
            </button>}
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
      {!isCliente && <CartAddedDialog />}
    </div>
  );
}
